import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Avatar,
  Stack,
  IconButton,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { AccountSettingsData } from '../types';
import HolographicCard from '../../../components/common/HolographicCard.tsx';
import GradientText from '../../../components/common/GradientText.tsx';

interface AccountSettingsProps {
  settings: AccountSettingsData;
  onUpdate: (type: string, data: any) => Promise<void>;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ settings, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(settings);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUpdate('account', formData);
    setEditing(false);
  };

  return (
    <HolographicCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <GradientText variant="h6" gradient="primary">Account Settings</GradientText>
        <IconButton 
          onClick={() => setEditing(!editing)}
          sx={{ 
            color: 'rgba(138, 43, 226, 0.8)',
            '&:hover': {
              color: 'rgba(0, 255, 255, 0.8)',
            }
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={settings.avatarUrl}
              sx={{ 
                width: 64, 
                height: 64,
                border: '2px solid rgba(138, 43, 226, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(0, 255, 255, 0.3)',
                }
              }}
            />
            <Button 
              variant="outlined" 
              component="label"
              sx={{
                borderColor: 'rgba(138, 43, 226, 0.3)',
                color: 'rgba(138, 43, 226, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(0, 255, 255, 0.3)',
                  color: 'rgba(0, 255, 255, 0.8)',
                  backgroundColor: 'rgba(0, 255, 255, 0.1)',
                }
              }}
            >
              Change Photo
              <input type="file" hidden accept="image/*" />
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            disabled={!editing}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            disabled={!editing}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Role"
            value={formData.role}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.2)',
                },
              },
            }}
          />

          {editing && (
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #8a2be2 30%, #00CC99 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7b1fa2 30%, #00b894 90%)',
                }
              }}
            >
              Save Changes
            </Button>
          )}
        </Stack>
      </form>
    </HolographicCard>
  );
};

export default AccountSettings; 